import * as globals from '@wandb/weave/common/css/globals.styles';
import {createContext, useContext} from 'react';

import {
  type UsageAggregationInterface,
  UsageType,
} from '../../generated/graphql';
import {UsageTabURLPart} from '../../routes/usage/usagePaths';
import {
  addMonthsUTC,
  compareAsc,
  DateFormat,
  format,
  isBefore,
  startOfDayUTC,
  subDaysUTC,
  subMonthsUTC,
} from '../../util/date';
import {type Bar} from '../../util/plotHelpers/types';
import {
  getEarliestUsageRolloutDate,
  type UsageAggregationTypes,
  type UsageTotal,
  valueAggregator,
} from '../../util/usage/utils';

// Set empty bar for non existent data a smaller proportion of the max value to give
// the tooltip a height to hover on
const EMPTY_BAR_PROPORTION_VALUE = 0.05;

export enum TimeRange {
  LAST_12_MONTHS = '12 months',
  LAST_24_MONTHS = '24 months',
}

export type ProcessedData = Bar & {end: Date; hideValue?: true};

function getKey(start: Date, end: Date) {
  return `${start.getUTCFullYear()}-${start.getUTCMonth()}-${start.getUTCDate()},${end.getUTCFullYear()}-${end.getUTCMonth()}-${end.getUTCDate()}`;
}

// Use UTC dates so that the months don't get messed up by local timezones
function getLabel(start: Date, end: Date) {
  return `${format(start, DateFormat.MONTH_DAY, 'UTC')} - ${format(
    end,
    DateFormat.DAY,
    'UTC'
  )}, ${format(start, DateFormat.YEAR, 'UTC')}:`;
}

function getXAxisLabel(start: Date) {
  return format(start, DateFormat.MONTH_YEAR, 'UTC');
}

export type UsageByDateMap = {
  [key: string]: {value: number; label: string; start: Date; end: Date};
};

export function toUsageByDateMap(
  usage: UsageTotal,
  intervalMarkers: Date[]
): UsageByDateMap {
  if (usage == null || usage.length + 1 !== intervalMarkers.length) {
    return {};
  }

  const usageByDateMap: UsageByDateMap = {};

  usage.forEach((value, i) => {
    const start = intervalMarkers[i];
    const end = subDaysUTC(intervalMarkers[i + 1], 1);
    const label = getLabel(start, end);
    const key = getKey(start, end);
    usageByDateMap[key] = {
      value,
      label,
      start,
      end,
    };
  });
  return usageByDateMap;
}

export function processUsageData(
  usage: UsageAggregationInterface[],
  usageType: UsageType,
  aggregationCutoffDate: Date | undefined,
  intervalMarkers: Date[]
): ProcessedData[] {
  // Group usage by bucket and sum
  const usageByDate = usage.reduce((acc, data) => {
    const start = new Date(data.start);
    const end = new Date(data.end);
    const key = getKey(start, end);
    if (key in acc) {
      acc[key].value = valueAggregator(
        data,
        usageType,
        acc[key].value,
        aggregationCutoffDate
      );
    } else {
      acc[key] = {
        value: valueAggregator(data, usageType, 0, aggregationCutoffDate),
        label: getLabel(start, end),
        start,
        end,
      };
    }
    return acc;
  }, {} as UsageByDateMap);

  // Check for missing data and pad with 0s - this handles data before aggregationCutoffDate or org.createdAt
  return processUsageTotalData(
    usageByDate,
    usageType,
    aggregationCutoffDate,
    intervalMarkers
  );
}

export function processUsageTotalData(
  usageByDate: UsageByDateMap,
  usageType: UsageType,
  aggregationCutoffDate: Date | undefined,
  intervalMarkers: Date[]
): ProcessedData[] {
  // Check for missing data and pad with 0s - this handles data before aggregationCutoffDate or org.createdAt
  intervalMarkers.forEach((start, i) => {
    if (i !== intervalMarkers.length - 1) {
      const end = subDaysUTC(intervalMarkers[i + 1], 1);
      const key = getKey(start, end);
      if (!(key in usageByDate)) {
        usageByDate[key] = {value: 0, label: getLabel(start, end), start, end};
      }
    }
  });

  const emptyBarValue = Math.max(
    ...Object.values(usageByDate).map(
      ({value}) => EMPTY_BAR_PROPORTION_VALUE * value
    ),
    1
  );

  return Object.keys(usageByDate)
    .sort((key1, key2) =>
      compareAsc(usageByDate[key1].start, usageByDate[key2].start)
    ) // sort by bucket start date
    .map(key => {
      const isBeforeInitialCutoff =
        aggregationCutoffDate != null &&
        isBefore(usageByDate[key].end, startOfDayUTC(aggregationCutoffDate));
      const earliestDataDate = getEarliestUsageRolloutDate(usageType);
      const isBeforeEarliestDataDate =
        earliestDataDate != null &&
        isBefore(usageByDate[key].end, earliestDataDate);

      if (isBeforeInitialCutoff || isBeforeEarliestDataDate) {
        return {
          key: getXAxisLabel(usageByDate[key].start),
          value: emptyBarValue,
          color: globals.hexToRGB(globals.MOON_300, 0.48),
          title: isBeforeEarliestDataDate
            ? `No data for this period. Data starts ${format(
                earliestDataDate,
                DateFormat.MONTH_DAY_YEAR,
                'UTC'
              )}`
            : 'New customer. No data for this period.',
          end: usageByDate[key].end,
          hideValue: true,
        };
      }
      return {
        key: getXAxisLabel(usageByDate[key].start),
        value: usageByDate[key].value,
        color: globals.hexToRGB(globals.MAGENTA_300, 0.48),
        title: usageByDate[key].label,
        end: usageByDate[key].end,
      };
    });
}

function getNIntervals(startOfNextMonth: Date, n: number) {
  return Array.from({length: n}, (x, i) =>
    subMonthsUTC(startOfNextMonth, i)
  ).reverse();
}

export function getIntervalMarkers(timeRange: TimeRange): Date[] {
  const startOfCurrentMonth = new Date();
  // Ignore local time zone
  startOfCurrentMonth.setUTCHours(0, 0, 0, 0);
  startOfCurrentMonth.setUTCDate(1);
  const startOfNextMonth = addMonthsUTC(startOfCurrentMonth, 1);
  switch (timeRange) {
    case TimeRange.LAST_12_MONTHS:
      // For n intervals, we need n + 1 interval markers
      return getNIntervals(startOfNextMonth, 13);
    case TimeRange.LAST_24_MONTHS:
      return getNIntervals(startOfNextMonth, 25);
  }
}

export function getUsageTab(tab: string | undefined): UsageType {
  switch (tab) {
    case UsageTabURLPart.TRACKED_HOURS:
      return UsageType.TrackedHours;
    case UsageTabURLPart.WEAVE:
      return UsageType.Weave;
    case UsageTabURLPart.INFERENCE_COST:
      return UsageType.InferenceCost;
    case UsageTabURLPart.TRAINING:
      return UsageType.TrainingCost;
    case UsageTabURLPart.WB_AGENT:
      return UsageType.WbAgentCost;
    case UsageTabURLPart.SANDBOXES:
      return UsageType.SandboxesCost;
    default:
      return UsageType.Storage;
  }
}

export type HistoricUsageChartContextValue = {
  processedData: ProcessedData[] | undefined;
  loading: boolean;
  setSelectedTimeRange: (timeRange: TimeRange) => void;
  selectedTimeRange: TimeRange;
  usageType: UsageType;
  error: Error | undefined;
  aggregationCutoffDate: Date | undefined;
  fetchCSVUsage: (
    usageType: UsageType,
    intervalMarkers: Date[]
  ) => Promise<UsageAggregationTypes[]>;
};

export const HistoricUsageChartContext =
  createContext<HistoricUsageChartContextValue | null>(null);

export function useHistoricUsageChartContext(): HistoricUsageChartContextValue {
  const context = useContext(HistoricUsageChartContext);
  if (context === null) {
    throw new Error(
      'useHistoricUsageChartContext must be used within a HistoricUsageChartContextProvider'
    );
  }
  return context;
}
